import Dexie, { Table } from 'dexie'
import { debugLog } from '@/utils/debugLog'

// 1️⃣ Define the data interface for consumers
export interface WaterMeter {
  id?: number
  waterCard: number
  contractNumber: number
  propertyNumber: number
  address: string
  usageType: string
  usageTypeCode: number
  householdSize: number
  tenantCount: number
  area: number
  avarageConsumption: number
  meterNumber: number
  diameterDn: number
  diameter: string
  meterType: string
  manufacturer: string
  issuer: string
  model: string
  rvalue: number
  shabbat: string
  endUnitNumber: string
  installed: string
  meterAge: number
  serviceTime: number
  walkOrder: string
  coordinates: Record<string, any>
  itmCoordinates: string
  hasPasGishur: boolean
  isSupplier: boolean
  lastSeen: string
  capacity: number
  dailyUsageData: { value: number; night?: number; date: string }[]
  name: string
  payerNumber: string
  midnightCapacity: number
  backwardCapacity: number
  consumption: number
  unitType: string
  order?: number
}

// 2️⃣ Define the data interface for your two-monthly readings
export interface TwoMonthlyRecord {
  id?: number
  consumerId: string // e.g. "20009008"
  meterNumber: string
  startTimestamp: string
  endTimestamp?: string
  consumption?: number
  hasAlerts: boolean
  hasReplacement: boolean
}

// 3️⃣ Hourly Data
export interface HourlyRecord {
  id?: number
  consumerId: string
  meterNumber: string
  timestamp: string
  value: number
  consumption: number
}

export interface DailyReading {
  meterNumber: string
  timestamp: string
  value: number
  consumption: number
  nightConsumption: number
}

// 5️⃣ Alerts
export interface AlertMessage {
  channel: string
  contact: string
  preview: string
  sentAt: string
}

export interface AlertRecord {
  id: number
  meterId: number
  consumerName: string
  severity: string
  alertType: string
  fromDate: string
  toDate: string
  isOpen: boolean
  waterCard: number
  messages?: AlertMessage[]
}

export interface ReplacementData {
  fromDate: string
  meterNumber: number
  reason: string
  images: {
    old: string
    new: string
  }
}

export interface Task {
  taskType: string
  description: string
  meterNumber: string
  assignedTo: string
  createdAt: string
  lastUpdated: string
  dueDate: string
  priority: string
  status: string
  updatedBy: string
}

export interface DashboardStats {
  date: string
  nightSupply: number
  supply: number
  nightConsumption: number
  consumption: number
}

export interface Sensor {
  id?: number
  address: string
  unitType: string
  endUnitNumber: string
  midnightValue: string
  valueType: string
  coordinates: {
    latitude: number
    longitude: number
  }
  lastSeen: string
  dailyUsageData: { value: number; night: number; date: string }[]
  name: string
}

export interface WaterTreeZone {
  id?: number
  name: string
  zoneId: number
  suppliers: number
  consumers: number
  supply: number
  nightSupply: number
  consumption: number
  nightConsumption: number
  loss: number
  nightLoss: number
  shared: number
}

export interface WaterTreePeriod {
  id?: number
  zoneId: number
  startDate: string
  endDate: string
  suppliers: number
  consumers: number
  lossPercent: number
  shared: number
  supply: number
  nightSupply: number
  consumption: number
  nightConsumption: number
  loss: number
}

export interface DailyUsageEntry {
  value: number
  night?: number
  date: string
}

export interface WaterConsumer {
  id: number
  zoneId: number
  waterCard: number
  contractNumber: number
  propertyNumber: number
  address: string
  usageType: string
  usageTypeCode: number
  householdSize: number
  tenantCount: number
  area: number
  avarageConsumption: number
  meterNumber: number
  diameterDn: number
  diameter: string
  meterType: string
  manufacturer: string
  issuer: string
  model: string
  rvalue: number
  q3value: number
  shabbat: string
  endUnitNumber: string
  installed: string
  meterAge: number
  serviceTime: number
  walkOrder: string
  coordinates: Record<string, any>
  itmCoordinates: string
  hasPasGishur: boolean
  isSupplier: boolean
  lastSeen: string
  capacity: number
  midnightCapacity: number
  backwardCapacity: number
  consumption: number
  unitType: string
  payerNumber: string
  name: string
  dailyUsageData: DailyUsageEntry[]
}

export interface ConsumptionRecord {
  id?: number
  propertyNumber: string
  readDate: string
  meterType: string
  address: string
  billingPeriod: string
  readValue: number
  preValue: number
  consumption: number
  shared: number | null
  parentNumber: string | null
  parentType: string | null
  parentReadingTime: string | null
  status: string | null
  parentStatus: string | null
}

export interface AlertTypeTwo {
  id?: number
  meterId: number
  consumerName: string
  severity: string
  alertType: string
  fromDate: string
  isOpen: boolean
  waterCard: number
  payerNumber: string
  address: string
  capacity: number
  lastSeen: string
}

export interface MeterChange {
  id?: number
  water_connection_number: number
  address: string
  measurement_zone: number
  consumer_name: string
  payer_number: string
  replacement_date: string
  replacement_reason: string
  old_meter_number: number
  new_meter_number: number
  old_meter_diameter: string
  new_meter_diameter: string
  removal_reading: number
  installation_reading: number
  old_meter_photo_url: string
  new_meter_photo_url: string
  reason_not_performed: string
}

export interface MeterPrepare {
  id?: number
  water_connection_number: number
  meter_number: number
  diameter: string
  measurement_zone: number
  consumer_name: string
  address: string
  payer_number: string
  installation_date: string
  bypass_bar_installation: string
  installation_code: number
  meter_photo_url: string
}

export interface MeterExpire {
  id?: number
  water_meter_number: string
  meter_id: string
  meter_diameter: string
  address: string
  measurement_zone: string
  consumer_name: string
  payer_number: string
  meter_type: string
  installation_date: string
  last_reading_date: string
}

export interface MeterIsuse {
  id?: number
  date: string
  waterCard: number
  meterNumber: number
  problemStart: string
}

export interface MeterCommunication {
  id?: number
  date: string
  area: number
  percent: number
}

// 3️⃣ Extend Dexie
export class MyAppDB extends Dexie {
  consumers!: Table<WaterMeter, number>
  singleConsumersTwoMonthly!: Table<TwoMonthlyRecord, number>
  singleConsumersHourly!: Table<HourlyRecord, number>
  // @ts-ignore
  singleConsumersDaily: Table<
    { meterNumber: string; readings: DailyReading[] },
    string
  >
  singleConsumersAlerts!: Table<AlertRecord, number>
  singleConsumersReplacement!: Table<ReplacementData, number>
  singleConsumersTasks!: Table<Task, number>
  DashboardStats!: Table<DashboardStats, number>

  sensors!: Table<Sensor, number>

  waterTreeZones!: Table<WaterTreeZone, number>
  waterTreePeriods!: Table<WaterTreePeriod, number>
  waterConsumers!: Table<WaterConsumer, number>

  consumptions!: Table<ConsumptionRecord, number>

  alertTypeTwo!: Table<AlertTypeTwo, number>

  meterChanges!: Table<MeterChange, number>
  meterPrepare!: Table<MeterPrepare, number>
  meterExpire!: Table<MeterExpire, number>
  meterIsuse!: Table<MeterIsuse, number>
  meterCommunication!: Table<MeterCommunication, number>

  constructor() {
    super('MyAppDB')

    this.version(12).stores({
      consumers: 'meterNumber,waterCard,contractNumber,address,name,order',
      singleConsumersTwoMonthly: '++id,consumerId,meterNumber,startTimestamp',
      singleConsumersHourly: '++id,consumerId,meterNumber,timestamp',
      singleConsumersDaily: '++id,consumerId,meterNumber,timestamp',
      singleConsumersAlerts: 'id,meterId,waterCard,severity,alertType,isOpen',
      singleConsumersReplacement: '++id,fromDate,meterNumber,reason',
      singleConsumersTasks:
        '++id,taskType,description,meterNumber,assignedTo,createdAt,lastUpdated,dueDate,priority,status,updatedBy',
      DashboardStats:
        '++id,date,nightSupply,supply,nightConsumption,consumption',
      sensors: '++id,endUnitNumber,address,name',

      waterTreeZones: '++id,zoneId,name',
      waterTreePeriods: '++id,zoneId,startDate,endDate',
      waterConsumers: '++id,zoneId,address,meterNumber,name',

      consumptions: '++id,propertyNumber,readDate,billingPeriod,parentNumber',

      alertTypeTwo: '++id,meterId,consumerName,waterCard,payerNumber',

      meterChanges:
        '++id,water_connection_number,consumer_name,payer_number,measurement_zone',
      meterPrepare:
        '++id,water_connection_number,meter_number,consumer_name,payer_number,measurement_zone',
      meterExpire:
        '++id,water_meter_number,meter_id,consumer_name,payer_number,measurement_zone',
      meterIsuse: '++id,waterCard,meterNumber,date',
      meterCommunication: '++id,date,area',
    })
  }
}

export const db = new MyAppDB()

// 4️⃣ Normal consumer initialization
export async function initDB(data: any[]) {
  const count = await db.consumers.count()
  if (count === 0) {
    // Add order field to preserve JSON array order
    const dataWithOrder = data.map((item, index) => ({
      ...item,
      order: index
    }))
    await db.consumers.bulkAdd(dataWithOrder)
  } else {
    debugLog('Consumers data already exists.')
  }
}

// 5️⃣ Two-monthly data initialization
export async function initDBTwoMonthly(rawData: Record<string, any[]>) {
  const count = await db.singleConsumersTwoMonthly.count()
  if (count === 0) {
    const flatData: TwoMonthlyRecord[] = []

    for (const [consumerId, records] of Object.entries(rawData)) {
      for (const record of records) {
        flatData.push({
          consumerId,
          meterNumber: record.meterNumber,
          startTimestamp: record.startTimestamp,
          endTimestamp: record.endTimestamp,
          consumption: record.consumption,
          hasAlerts: record.hasAlerts,
          hasReplacement: record.hasReplacement,
        })
      }
    }

    debugLog(`Inserting ${flatData.length} two-monthly records...`)
    await db.singleConsumersTwoMonthly.bulkAdd(flatData)
  } else {
    debugLog('Two-monthly data already exists.')
  }
}

// 7️⃣ Hourly initialization
export async function initDBHourly(rawData: Record<string, any[]>) {
  const count = await db.singleConsumersHourly.count()
  if (count === 0) {
    const flatData: HourlyRecord[] = []

    for (const [consumerId, records] of Object.entries(rawData)) {
      for (const record of records) {
        flatData.push({
          consumerId,
          meterNumber: record.meterNumber,
          timestamp: record.timestamp,
          value: record.value,
          consumption: record.consumption,
        })
      }
    }

    debugLog(`Inserting ${flatData.length} hourly records...`)
    await db.singleConsumersHourly.bulkAdd(flatData)
  } else {
    debugLog('Hourly data already exists.')
  }
}

// 9️⃣ Initialize Daily
export async function initDBDaily(rawData: Record<string, any[]>) {
  const count = await db.singleConsumersDaily.count()
  if (count === 0) {
    const flatData: any[] = []

    for (const [consumerId, records] of Object.entries(rawData)) {
      for (const record of records) {
        flatData.push({
          consumerId,
          meterNumber: record.meterNumber,
          timestamp: record.timestamp,
          value: record.value,
          consumption: record.consumption,
          nightConsumption: record.nightConsumption,
        })
      }
    }

    debugLog(`Inserting ${flatData.length} daily records...`)
    await db.singleConsumersDaily.bulkAdd(flatData)
  } else {
    debugLog('Daily data already exists.')
  }
}

// ✅ Alerts
export async function initDBAlerts(alerts: any[]) {
  const count = await db.singleConsumersAlerts.count()
  if (count === 0) {
    debugLog(`Inserting ${alerts.length} alerts...`)
    await db.singleConsumersAlerts.bulkAdd(alerts)
  } else {
    debugLog('Alerts data already exists.')
  }
}

// Initialize replacements
export async function initReplacements(data: Record<string, any[]>) {
  const count = await db.singleConsumersReplacement.count()
  if (count === 0) {
    const flat = Object.values(data).flat()
    await db.singleConsumersReplacement.bulkAdd(flat)
  }
}

// Initialize tasks
export async function initTasks(data: any[]) {
  const count = await db.singleConsumersTasks.count()
  if (count === 0) {
    const flat = Object.values(data).flat()
    await db.singleConsumersTasks.bulkAdd(flat)
  }
}

export async function initDashboardStats(data: any[]) {
  const count = await db.DashboardStats.count()
  if (count === 0) {
    await db.DashboardStats.bulkAdd(data)
  }
}

export async function initSensors(data: Sensor[]) {
  const count = await db.sensors.count()
  if (count === 0) {
    debugLog(`Inserting ${data.length} sensors...`)
    await db.sensors.bulkAdd(data)
  } else {
    debugLog('Sensors data already exists.')
  }
}

export async function initWaterTreeZones(data: WaterTreeZone[]) {
  const count = await db.waterTreeZones.count()
  if (count === 0) {
    debugLog(`Inserting ${data.length} water tree zones...`)
    await db.waterTreeZones.bulkAdd(data)
  } else {
    debugLog('Water tree data already exists.')
  }
}

export async function initWaterTreePeriods(periodData: Record<string, any[]>) {
  const count = await db.waterTreePeriods.count()
  if (count === 0) {
    debugLog(`Inserting ${periodData.length} water tree periods...`)
    const flattened: WaterTreePeriod[] = Object.entries(periodData).flatMap(
      ([zoneId, records]) =>
        records.map(r => ({ ...r, zoneId: Number(zoneId) }))
    )
    await db.waterTreePeriods.bulkAdd(flattened)
  } else {
    debugLog('Water tree period data already exists.')
  }
}

export async function initWaterConsumers(consumerData: Record<string, any[]>) {
  const count = await db.waterConsumers.count()
  if (count === 0) {
    const flattened: WaterConsumer[] = Object.entries(consumerData).flatMap(
      ([zoneId, records]) =>
        records.map(r => ({ ...r, zoneId: Number(zoneId) }))
    )
    debugLog(`Inserting ${flattened.length} water consumers...`)
    await db.waterConsumers.bulkAdd(flattened)
  } else {
    debugLog('Water consumers already exist.')
  }
}
export async function initConsumptions(data: ConsumptionRecord[]) {
  const count = await db.consumptions.count()
  if (count === 0) {
    debugLog(`Inserting ${data.length} consumption records...`)
    await db.consumptions.bulkAdd(data)
  } else {
    debugLog('Consumptions data already exists.')
  }
}

export async function initAlertTypeTwo(data: AlertTypeTwo[]) {
  const count = await db.alertTypeTwo.count()
  if (count === 0) {
    debugLog(`Inserting ${data.length} alertTypeTwo records...`)
    await db.alertTypeTwo.bulkAdd(data)
  } else {
    debugLog('AlertTypeTwo data already exists.')
  }
}

export async function initMeterChanges(data: any[]) {
  const count = await db.meterChanges.count()
  if (count === 0) {
    debugLog(`Inserting ${data.length} meter change records...`)
    await db.meterChanges.bulkAdd(data)
  } else {
    debugLog('Meter change data already exists.')
  }
}

export async function initMeterPrepare(data: any[]) {
  const count = await db.meterPrepare.count()
  if (count === 0) {
    debugLog(`Inserting ${data.length} meter prepare records...`)
    await db.meterPrepare.bulkAdd(data)
  } else {
    debugLog('MeterPrepare data already exists.')
  }
}

export async function initMeterExpire(data: any[]) {
  const count = await db.meterExpire.count()
  if (count === 0) {
    debugLog(`Inserting ${data.length} meter expire records...`)
    await db.meterExpire.bulkAdd(data)
  } else {
    debugLog('MeterExpire data already exists.')
  }
}

export async function initMeterIsuse(data: MeterIsuse[]) {
  const count = await db.meterIsuse.count()
  if (count === 0) {
    debugLog(`Inserting ${data.length} meter isuse records...`)
    await db.meterIsuse.bulkAdd(data)
  } else {
    debugLog('MeterIsuse data already exists.')
  }
}

export async function initMeterCommunication(data: MeterCommunication[]) {
  const count = await db.meterCommunication.count()
  if (count === 0) {
    debugLog(`Inserting ${data.length} meter communication records...`)
    await db.meterCommunication.bulkAdd(data)
  } else {
    debugLog('MeterCommunication data already exists.')
  }
}

// 6️⃣ Helper getters
export async function getRows(limit = 1000) {
  return await db.consumers.orderBy('order').limit(limit).toArray()
}

export async function getAllRows() {
  return await db.consumers.orderBy('order').toArray()
}

export async function getRowsByMeterNumber(meterNumber: string) {
  return await db.consumers
    .where('meterNumber')
    .equals(parseInt(meterNumber))
    .toArray()
}

export async function getRowsByPage(page = 0, pageSize = 10) {
  return await db.consumers
    .offset(page * pageSize)
    .limit(pageSize)
    .toArray()
}

export async function getAllTwoMonthlyRows() {
  return await db.singleConsumersTwoMonthly.toArray()
}

// 9️⃣ Fetch by Consumer ID
export async function getHourlyByConsumer(consumerId: string) {
  return await db.singleConsumersHourly
    .where('meterNumber')
    .equals(consumerId)
    .toArray()
}

export async function getTwoMonthlyByMeterNumber(meterNumber: string) {
  return await db.singleConsumersTwoMonthly
    .where('meterNumber')
    .equals(meterNumber)
    .toArray()
}

export async function getDailyByConsumer(consumerId: string) {
  return await db.singleConsumersDaily
    .where('meterNumber')
    .equals(consumerId)
    .toArray()
}

export async function getDailyByMeterNumber(meterNumber: string) {
  return await db.singleConsumersDaily
    .where('meterNumber')
    .equals(meterNumber)
    .toArray()
}

export async function getDailyByTimestamp(timestamp: string) {
  return await db.singleConsumersDaily
    .where('timestamp')
    .equals(timestamp)
    .toArray()
}

export async function getAlertsByMeterNumber(meterNumber: number) {
  return await db.singleConsumersAlerts
    .where('meterId')
    .equals(meterNumber)
    .toArray()
}

export async function getAllAlerts() {
  return await db.singleConsumersAlerts.toArray()
}

export async function getReplacementsByMeterNumber(meterNumber: number) {
  return await db.singleConsumersReplacement
    .where('meterNumber')
    .equals(meterNumber)
    .toArray()
}

export async function getTasksByMeterNumber(meterNumber: string) {
  return await db.singleConsumersTasks
    .where('meterNumber')
    .equals(meterNumber)
    .toArray()
}

export async function getTasks() {
  return await db.singleConsumersTasks.toArray()
}

export async function getDashboardStats() {
  return await db.DashboardStats.toArray()
}

export async function getAllSensors() {
  return await db.sensors.toArray()
}

export async function getSensorById(id: number) {
  return await db.sensors.where('id').equals(id).first()
}

export async function getSensorByEndUnitNumber(endUnitNumber: string) {
  return await db.sensors.where('endUnitNumber').equals(endUnitNumber).first()
}

export async function getSensorByName(name: string) {
  return await db.sensors.where('name').equals(name).first()
}

export async function getAllWaterTreeZones() {
  return await db.waterTreeZones.toArray()
}

export async function getWaterTreeZoneById(zoneId: number) {
  return await db.waterTreeZones.where('zoneId').equals(zoneId).first()
}

export async function getWaterTreeZonesByName(name: string) {
  return await db.waterTreeZones.where('name').equalsIgnoreCase(name).toArray()
}

export async function getPeriodsByZoneId(zoneId: number) {
  return await db.waterTreePeriods.where('zoneId').equals(zoneId).toArray()
}

export async function getLatestPeriodByZoneId(zoneId: number) {
  const all = await db.waterTreePeriods.where('zoneId').equals(zoneId).toArray()
  return all.sort(
    (a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
  )[0]
}

export async function getAllPeriods() {
  return await db.waterTreePeriods.toArray()
}

export async function getConsumersByZoneId(zoneId: number) {
  return await db.waterConsumers.where('zoneId').equals(zoneId).toArray()
}

export async function getConsumerById(id: number) {
  return await db.waterConsumers.get(id)
}

export async function getAllConsumers() {
  return await db.waterConsumers.toArray()
}

export async function getAllConsumptions() {
  return await db.consumptions.toArray()
}

export async function getConsumptionsByProperty(propertyNumber: string) {
  return await db.consumptions
    .where('propertyNumber')
    .equals(propertyNumber)
    .toArray()
}

export async function getConsumptionsByParent(parentNumber: string) {
  return await db.consumptions
    .where('parentNumber')
    .equals(parentNumber)
    .toArray()
}

export async function getConsumptionsByBillingPeriod(period: string) {
  return await db.consumptions.where('billingPeriod').equals(period).toArray()
}

export async function getAllAlertTypeTwo() {
  return await db.alertTypeTwo.toArray()
}

export async function getAlertsByMeter(meterId: number) {
  return await db.alertTypeTwo.where('meterId').equals(meterId).toArray()
}

export async function getAlertsByWaterCard(waterCard: number) {
  return await db.alertTypeTwo.where('waterCard').equals(waterCard).toArray()
}

export async function getAlertsBySeverity(severity: string) {
  return await db.alertTypeTwo.where('severity').equals(severity).toArray()
}

export async function getAllMeterChanges() {
  return await db.meterChanges.toArray()
}

export async function getMeterChangesByZone(zone: number) {
  return await db.meterChanges.where('measurement_zone').equals(zone).toArray()
}

export async function getMeterChangesByPayer(payerNumber: string) {
  return await db.meterChanges
    .where('payer_number')
    .equals(payerNumber)
    .toArray()
}

export async function getMeterChangeByConnection(connectionNumber: number) {
  return await db.meterChanges
    .where('water_connection_number')
    .equals(connectionNumber)
    .first()
}

export async function getAllMeterPrepare() {
  return await db.meterPrepare.toArray()
}

export async function getMeterPrepareByZone(zone: number) {
  return await db.meterPrepare.where('measurement_zone').equals(zone).toArray()
}

export async function getMeterPrepareByConnection(connectionNumber: number) {
  return await db.meterPrepare
    .where('water_connection_number')
    .equals(connectionNumber)
    .first()
}

export async function getMeterPrepareByPayer(payerNumber: string) {
  return await db.meterPrepare
    .where('payer_number')
    .equals(payerNumber)
    .toArray()
}

export async function getAllMeterExpire() {
  return await db.meterExpire.toArray()
}

export async function getMeterExpireByZone(zone: string) {
  return await db.meterExpire.where('measurement_zone').equals(zone).toArray()
}

export async function getMeterExpireByPayer(payerNumber: string) {
  return await db.meterExpire
    .where('payer_number')
    .equals(payerNumber)
    .toArray()
}

export async function getMeterExpireByMeterId(meterId: string) {
  return await db.meterExpire.where('meter_id').equals(meterId).first()
}

export async function getMeterExpireByConnection(meterNumber: string) {
  return await db.meterExpire
    .where('water_meter_number')
    .equals(meterNumber)
    .first()
}

export async function getAllMeterIsuse() {
  return await db.meterIsuse.toArray()
}

export async function getMeterIsuseByMeter(meterNumber: number) {
  return await db.meterIsuse.where('meterNumber').equals(meterNumber).toArray()
}

export async function getMeterIsuseByWaterCard(waterCard: number) {
  return await db.meterIsuse.where('waterCard').equals(waterCard).toArray()
}

export async function getMeterIsuseByDate(date: string) {
  return await db.meterIsuse.where('date').equals(date).toArray()
}

export async function getAllMeterCommunication() {
  return await db.meterCommunication.toArray()
}

export async function getMeterCommunicationByDate(date: string) {
  return await db.meterCommunication.where('date').equals(date).toArray()
}

export async function getMeterCommunicationByArea(area: number) {
  return await db.meterCommunication.where('area').equals(area).toArray()
}
